/** images */
import React, { useEffect, useState, useContext } from 'react';

import { NotificationManager } from 'react-notifications';
import { useNavigate } from 'react-router-dom';

/** components */
import { RenderFieldFn } from './PageForm/helpersForPageForm/RenderFieldFn';

import deleteImg from '../assets/images/delete.png';
import editImg from '../assets/images/edit.png';

/** context */
import { UserContext } from '../context/UserContext';

/** helpers */
import { formatNumber } from '../helpers/format';
import { validateInputField } from '../helpers/validation';
import { useRudder } from '../hooks/rudder';

/** service */
import { updateApplication, validateProperty } from '../service/api';

const OwnerItem = ({
  owner,
  owners,
  handleUpdateOwners,
  index,
  handleSaveFileProperty,
  renderFieldProps,
  getPropertyFields,
  expanded,
  isEdit,
  sanitizeData,
  setError,
  isMainApplicant,
  isLoggedinUser,
  handleSkipToNextSection,
  removeCreateForm,
}) => {
  const navigate = useNavigate();

  const { appData, profile, updateAppData } = useContext(UserContext);
  const { errorEvent } = useRudder();
  const features = appData.applicationFeatures;
  const applicantFeature = features?.find((feature) => feature.itemType === 'ApplicantProperty');

  const [ownerData, setOwnerData] = useState(owner);
  const [ownerDataError, setOwnerDataError] = useState(owner.errors ? owner.errors : {});
  const [editable, setEditable] = useState(expanded);
  const [sectionError, setSectionError] = useState('');

  useEffect(() => {
    setOwnerData(owner);
    setOwnerDataError(owner.errors ? owner.errors : {});
  }, [owner]);

  const handleSubmitApplicant = async (data) => {
    // Replace owners info for the current index
    const applicantsData = owners.map((o, i) => {
      if (i == index) {
        return {
          ...data,
        };
      }
      return o;
    });
    try {
      const updatedData = await updateApplication([
        {
          itemType: 'ApplicantProperty',
          dataType: 'applicant',
          name: 'applicant',
          value: applicantsData,
        },
      ]);

      if (isEdit) {
        // when we save OwnerItem from edit mode, and go back to review page, should update appData
        if (updatedData.data) {
          updateAppData(updatedData.data);
        }
      }

      const validationData = await validateProperty('applicant');
      setOwnerDataError({});

      if (validationData && validationData.data && validationData.data.errors) {
        const errorsArray = [{}, {}, {}, {}, {}];
        let hasError = false;
        errorEvent('Error Validate', validationData.data.errors);
        validationData.data.errors.map((error) => {
          /** Is submitted no applicant, should show applicant level error */
          if (error.property === 'applicant') {
            setError(error.message);
            hasError = true;
            return false;
          }
          const attributes = error.property.split('.');

          const ownerIndex = attributes[1];
          const ownerAttribute = attributes[2];
          errorsArray[ownerIndex] = {
            ...errorsArray[ownerIndex],
            [ownerAttribute]: error.message,
          };
        });
        errorsArray.forEach((errors, eIndex) => {
          if (Object.keys(errors).length > 0) {
            hasError = true;
            if (eIndex == index) {
              setOwnerDataError(errors);
            }
          }
        });
        return !hasError;
      }
    } catch (e) {
      console.log(e);
      errorEvent('Error API', e);
      NotificationManager.warning("The application couldn't be updated");
      return false;
    }
    return true;
  };

  const updateOwnerData = async () => {
    const fields = getPropertyFields(applicantFeature, ownerData);

    const result = fields.map((field) => {
      // No validation.js check for depending questions when the condition isn't met
      if (field.dependsOn) {
        if (Array.isArray(ownerData[field.dependsOn])) {
          if (!ownerData[field.dependsOn].includes(field.condition)) return true;
        } else {
          if (field.condition?.toString() !== ownerData[field.dependsOn]?.toString()) return true;
        }
      }
      // No validation.js check for file type or hidden type
      if (field.type === 'file' || field.type === 'hidden') {
        return true;
      }
      return validateInputField(ownerData[field.name], field.name, field, setOwnerDataError);
    });

    const isInValid = result.filter((r) => !r).length > 0;

    isInValid
      ? setSectionError('Please make sure all fields are filled in correctly.')
      : setSectionError('');

    if (isInValid) {
      return false;
    }

    const sanitizedData = sanitizeData(ownerData);

    const res = await handleSubmitApplicant(sanitizedData);
    if (res) {
      handleUpdateOwners();
      removeCreateForm();
      setEditable(false);
      if (isEdit) {
        navigate('/review');
      }
    }
  };

  const deleteOwner = async (email) => {
    const applicantsData = owners.filter((o) => {
      return o.email != email;
    });

    try {
      const updatedData = await updateApplication([
        {
          itemType: 'ApplicantProperty',
          dataType: 'applicant',
          name: 'applicant',
          value: applicantsData,
        },
      ]);

      if (updatedData.data) {
        updateAppData(updatedData.data);
      }

      const validationData = await validateProperty('applicant');
      setOwnerDataError({});

      if (validationData.data && validationData.data.errors) {
        const errorsArray = [{}, {}, {}, {}, {}];
        errorEvent('Error Validate', validationData.data.errors);
        validationData.data.errors.map((error) => {
          /** Is submitted no applicant, should show applicant level error */
          if (error.property === 'applicant') {
            console.error('Application Error', error.message);
            return false;
          }
          const attributes = error.property.split('.');

          const ownerIndex = attributes[1];
          const ownerAttribute = attributes[2];
          errorsArray[ownerIndex] = {
            ...errorsArray[ownerIndex],
            [ownerAttribute]: error.message,
          };
        });
        let hasError = false;
        errorsArray.forEach((errors, eIndex) => {
          if (Object.keys(errors).length > 0) {
            hasError = true;
            if (eIndex != index) {
              setOwnerDataError(errors);
            }
          }
        });
        handleUpdateOwners();
        return !hasError;
      }
    } catch (e) {
      console.log(e);
      setSectionError('There is an error deleting the owner');
      errorEvent('Error API', e);
    }
  };

  const getClassName = () => {
    let baseClassName = 'owner-item-inner';
    let error = false;
    Object.keys(ownerDataError).map((key) => {
      if (ownerDataError[key]) {
        error = true;
      }
    });
    baseClassName += error ? ' error' : '';
    return baseClassName;
  };
  const fields = getPropertyFields(applicantFeature, ownerData);
  const subSections = [
    ...new Set(
      fields.sort((field1, field2) => field1.order - field2.order).map((field) => field.subSection)
    ),
  ];

  return (
    <div className={'owner-item'} key={`owner${index}`}>
      <div className={getClassName()}>
        <div className="owner-title d-flex">
          <p>
            Owner Details - {owner.firstName} {owner.lastName}
          </p>
          {(isMainApplicant || isLoggedinUser) && !editable && (
            <img src={editImg} alt="" onClick={() => setEditable(true)} />
          )}
        </div>
        {(isMainApplicant || isLoggedinUser) && editable && (
          <div className="edit-form">
            {subSections.map((subSection, subIndex) => {
              const subSectionFields = fields.filter((field) => field.subSection == subSection);
              const nbOfVisibleFieldInsubtSection = subSectionFields.filter(
                (field) => field.type !== 'hidden'
              );
              if (subSectionFields.length == 0) return;
              return (
                <div className="subSection" key={'subSection' + subIndex}>
                  {nbOfVisibleFieldInsubtSection.length > 0 && subSection && (
                    <div className="subSectionTitle">{subSection}</div>
                  )}
                  <div className="form-list">
                    {subSectionFields
                      .sort((field1, field2) => field1.order - field2.order)
                      .map((field) =>
                        RenderFieldFn(
                          field,
                          {
                            ...renderFieldProps,
                            data: ownerData,
                            setData: setOwnerData,
                            dataError: ownerDataError,
                            setDataError: setOwnerDataError,
                          },
                          owners,
                          handleSaveFileProperty,
                          fields
                        )
                      )}
                  </div>
                </div>
              );
            })}

            {sectionError && <p className="section-error">{sectionError}</p>}
            <div className="actions">
              <button onClick={updateOwnerData} className="primary-btn">
                {isEdit ? 'Save and Return to Application Summary' : 'Save'}
              </button>
            </div>
          </div>
        )}
      </div>
      {isMainApplicant && owners.length > 1 && owner.email !== profile.email && (
        <div className="remove-owner d-flex" onClick={() => deleteOwner(owner.email)}>
          <img src={deleteImg} alt="" />
          <span>Remove this business owner</span>
        </div>
      )}
    </div>
  );
};

export default OwnerItem;
